<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineEmits } from 'vue';
import LoadingSpinner from './LoadingSpinner.vue';
import { useZenMode } from '../composables/useZenMode';

const emit = defineEmits(['toggle-zen-mode']);
const { isZenMode } = useZenMode();

interface Message {
  text: string
  isUser: boolean
  questionIndex?: number
  loading?: boolean
  citations?: Citation[]
  highlights?: Highlight[]
  suggestedReadings?: string[]
}

interface Citation {
  title: string
  url?: string
  text: string
  id?: string
}

interface Highlight {
  text: string
  citation_id: string
}

defineProps<{
  leftMessages: Message[]
  rightMessages: Message[]
  userQuestions: string[]
  leftPhilosopherName: string
  rightPhilosopherName: string
}>();

// Don't need isZenMode here since we're using props and emitting events

function handleToggleZenMode() {
  emit('toggle-zen-mode');
}
const modalRef = ref<HTMLDivElement | null>(null);
const lastActiveElement = ref<HTMLElement | null>(null);

// Helper function to add citation markers to text
function addCitationMarkers(text: string, citations: Citation[]): string {
  if (!citations || citations.length === 0) return text;
  
  let result = text;
  
  // Only add citation markers if not in zen mode
  if (!isZenMode.value) {
    citations.forEach((citation, index) => {
      const positionMatch = citation.text.match(/position: (\d+)/);
      if (positionMatch && positionMatch[1]) {
        const position = parseInt(positionMatch[1]);
        if (!isNaN(position) && position > 0 && position < result.length) {
          const beforePos = result.substring(0, position);
          const afterPos = result.substring(position);
          result = beforePos + `<sup class="citation-marker">[${index + 1}]</sup>` + afterPos;
        }
      }
    });
  }
  
  return result.split('\n\n').map(para => `<p>${para}</p>`).join('');
}

async function copyToClipboard(text: string, event?: MouseEvent) {
  console.log('ZenModeView: copyToClipboard called', { text, event })
  try {
    console.log('ZenModeView: Before calling navigator.clipboard.writeText')
    await navigator.clipboard.writeText(text);
    console.log('ZenModeView: Text copied successfully')
    
    // Show feedback that copy was successful only if event is provided
    if (event && event.currentTarget) {
      const button = event.currentTarget as HTMLElement;
      const originalInnerHTML = button.innerHTML;
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `;
      
      // Restore original icon after 1.5 seconds
      setTimeout(() => {
        if (button) {
          button.innerHTML = originalInnerHTML;
        }
      }, 1500);
    }
  } catch (err: unknown) {
    console.error('ZenModeView: Failed to copy text:', err);
    if (err instanceof Error) {
      console.log('ZenModeView: Error details:', { name: err.name, message: err.message, stack: err.stack })
    }
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    handleToggleZenMode();
  }
}

function trapFocus(event: KeyboardEvent) {
  if (!modalRef.value || event.key !== 'Tab') return;
  
  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length === 0) return;
  
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
  
  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement.focus();
    event.preventDefault();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement.focus();
    event.preventDefault();
  }
}

onMounted(() => {
  lastActiveElement.value = document.activeElement as HTMLElement;
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keydown', trapFocus);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keydown', trapFocus);
  lastActiveElement.value?.focus();
});
</script>

<template>
  <div class="zen-mode-container" ref="modalRef" tabindex="-1" role="dialog" aria-modal="true">
    <button
      class="zen-mode-close"
      @click="handleToggleZenMode"
      aria-label="Exit Zen Mode"
      title="Exit Zen Mode"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
      </svg>
    </button>
    <div class="zen-mode-content">
      <div v-for="(question, qIndex) in userQuestions" :key="'zen-entry-' + qIndex" class="zen-conversation">
        <div class="zen-user-question">{{ question }}</div>
        <div class="zen-responses">
          <div class="zen-response left-response">
            <h3 class="philosopher-name">{{ leftPhilosopherName }}</h3>
            <div v-if="leftMessages.find(m => m.questionIndex === qIndex)?.loading" class="loading-indicator">
              <LoadingSpinner />
            </div>
            <div v-else class="response-text">
              <button class="copy-button" @click="(event) => copyToClipboard(leftMessages.find(m => m.questionIndex === qIndex)?.text || '', event)" aria-label="Copy text">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              <div v-if="leftMessages.find(m => m.questionIndex === qIndex)?.citations?.length" v-html="addCitationMarkers(leftMessages.find(m => m.questionIndex === qIndex)?.text || '', leftMessages.find(m => m.questionIndex === qIndex)?.citations || [])"></div>
              <template v-else>
                <p v-for="(para, idx) in (leftMessages.find(m => m.questionIndex === qIndex)?.text || '').split('\n\n')" :key="idx">{{ para }}</p>
              </template>
              <div v-if="leftMessages.find(m => m.questionIndex === qIndex)?.citations?.length && !isZenMode" class="citations">
                <h4>Citations</h4>
                <div v-for="(citation, idx) in leftMessages.find(m => m.questionIndex === qIndex)?.citations" :key="'left-citation-' + idx" class="citation-item">
                  <span class="citation-number">{{ idx + 1 }}</span>
                  <span>{{ citation.title }}</span>
                </div>
              </div>
              <div v-if="leftMessages.find(m => m.questionIndex === qIndex)?.suggestedReadings?.length && !isZenMode" class="suggested-readings">
                <h4>Suggested Readings</h4>
                <ul>
                  <li v-for="(reading, idx) in leftMessages.find(m => m.questionIndex === qIndex)?.suggestedReadings" :key="'left-reading-' + idx">{{ reading }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="zen-response right-response">
            <h3 class="philosopher-name">{{ rightPhilosopherName }}</h3>
            <div v-if="rightMessages.find(m => m.questionIndex === qIndex)?.loading" class="loading-indicator">
              <LoadingSpinner />
            </div>
            <div v-else class="response-text">
              <button class="copy-button" @click="(event) => copyToClipboard(rightMessages.find(m => m.questionIndex === qIndex)?.text || '', event)" aria-label="Copy text">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              <div v-if="rightMessages.find(m => m.questionIndex === qIndex)?.citations?.length" v-html="addCitationMarkers(rightMessages.find(m => m.questionIndex === qIndex)?.text || '', rightMessages.find(m => m.questionIndex === qIndex)?.citations || [])"></div>
              <template v-else>
                <p v-for="(para, idx) in (rightMessages.find(m => m.questionIndex === qIndex)?.text || '').split('\n\n')" :key="idx">{{ para }}</p>
              </template>
              <div v-if="rightMessages.find(m => m.questionIndex === qIndex)?.citations?.length && !isZenMode" class="citations">
                <h4>Citations</h4>
                <div v-for="(citation, idx) in rightMessages.find(m => m.questionIndex === qIndex)?.citations" :key="'right-citation-' + idx" class="citation-item">
                  <span class="citation-number">{{ idx + 1 }}</span>
                  <span>{{ citation.title }}</span>
                </div>
              </div>
              <div v-if="rightMessages.find(m => m.questionIndex === qIndex)?.suggestedReadings?.length && !isZenMode" class="suggested-readings">
                <h4>Suggested Readings</h4>
                <ul>
                  <li v-for="(reading, idx) in rightMessages.find(m => m.questionIndex === qIndex)?.suggestedReadings" :key="'right-reading-' + idx">{{ reading }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.zen-mode-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #f8f9fa;
  z-index: 1100;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.3s ease-out;
}

.zen-mode-close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 30px;
  height: 24px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.7);
  color: #2c77b8;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 1200;
}

.zen-mode-close:hover {
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.zen-mode-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.zen-conversation {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
}

.zen-user-question {
  background-color: #1976d2;
  color: white;
  padding: 12px 20px;
  border-radius: 18px;
  align-self: flex-end;
  max-width: 80%;
  margin-bottom: 20px;
  font-size: 17px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.zen-responses {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.zen-response {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.philosopher-name {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 1.3rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 10px;
  font-weight: 600;
  color: #333;
}

.left-response {
  background-color: #e3f2fd;
  border: 1px solid #90c0ff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.right-response {
  background-color: #ffebee;
  border: 1px solid #ff9090;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.response-text {
  font-size: 18px;
  line-height: 1.7;
  position: relative;
  color: #333;
  letter-spacing: 0.01em;
}

.response-text p {
  margin: 0 0 18px;
  text-indent: 1.5em;
}

.copy-button {
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.copy-button:hover {
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.left-response .copy-button {
  color: #2c77b8;
}

.right-response .copy-button {
  color: #b82c2c;
}

.citations, .suggested-readings {
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 16px;
}

.citations h4, .suggested-readings h4 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
  font-weight: 600;
}

.citation-item {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  align-items: center;
}

.citation-number {
  background-color: rgba(0, 0, 0, 0.05);
  width: 24px;
  height: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

.citation-marker {
  color: #0084ff;
  font-weight: bold;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (max-width: 768px) {
  .zen-mode-content {
    padding: 15px;
  }
  
  .zen-response {
    padding: 15px;
  }
  
  .zen-mode-close {
    top: 15px;
    right: 15px;
    width: 36px;
    height: 36px;
  }
  
  .zen-user-question {
    max-width: 90%;
  }
}

@media (min-width: 769px) {
  .zen-responses {
    flex-direction: row;
  }
  
  .zen-response {
    flex: 1;
  }
}
</style>